import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getServiceImageUrl } from '../config';
import { toast } from '../components/Toast';
import logger from '../utils/logger';

const MyServices = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Limite globale de services pour tous les utilisateurs
  const MAX_SERVICES = 7;

  useEffect(() => {
    if (!user || (user.role !== 'author' && user.role !== 'admin')) {
      navigate('/services');
      return;
    }
    loadMyServices();
  }, [user, navigate]);

  const loadMyServices = async () => {
    try {
      const servicesRes = await api.get('/services/my');
      setServices(servicesRes.data.services || []);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur peut créer un nouveau service
  const canCreateService = () => {
    return services.length < MAX_SERVICES;
  };

  // Obtenir le nombre de services restants
  const getRemainingServices = () => {
    return Math.max(0, MAX_SERVICES - services.length);
  };

  // Obtenir la limite actuelle
  const getCurrentLimit = () => {
    return MAX_SERVICES;
  };

  // Supprimer un service
  const handleDeleteService = async () => {
    if (!deleteModal) return;
    
    setDeleting(true);
    try {
      await api.delete(`/services/${deleteModal.id}`);
      setServices(services.filter(s => s.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression du service');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-emerald-700">Mes Services</h1>
            <p className="text-gray-600 mt-1">Gérez vos services</p>
          </div>
          {canCreateService() ? (
            <Link
              to="/create-service"
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition flex items-center gap-2 justify-center"
            >
              ➕ Créer un service
            </Link>
          ) : (
            <div className="text-right">
              <button
                disabled
                className="px-6 py-3 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed flex items-center gap-2 justify-center"
              >
                🔒 Limite atteinte
              </button>
            </div>
          )}
        </div>

        {/* Bannière abonnement et limite */}
        {/* Bannière limite de services */}
        <div className="bg-white rounded-xl shadow p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-emerald-100">
                🛍️
              </div>
              <div>
                <p className="font-medium text-gray-800">
                  Limite de services
                </p>
                <p className="text-sm text-gray-500">
                  {services.length} / {getCurrentLimit()} services utilisés
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {getRemainingServices() > 0 ? (
                <span className="text-sm text-emerald-600 font-medium">
                  ✓ {getRemainingServices()} service{getRemainingServices() > 1 ? 's' : ''} restant{getRemainingServices() > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-sm text-amber-600 font-medium">
                  ⚠ Limite atteinte
                </span>
              )}
            </div>
          </div>
          
          {/* Barre de progression */}
          <div className="mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(100, (services.length / getCurrentLimit()) * 100)}%`,
                  backgroundColor: services.length >= getCurrentLimit() ? '#F59E0B' : '#10B981'
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Liste des services */}
        {services.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🛍️</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Aucun service créé</h2>
            <p className="text-gray-600 mb-6">Créez votre premier service pour le proposer sur la marketplace</p>
            {canCreateService() && (
              <Link
                to="/create-service"
                className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition"
              >
                Créer mon premier service
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {services.map(service => (
              <div key={service.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Image/Icône */}
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      {service.photos ? (
                        <img 
                          src={getServiceImageUrl(JSON.parse(service.photos)[0])}
                          alt={service.titre}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span className="text-white text-2xl">🛍️</span>
                      )}
                    </div>
                    
                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-bold text-gray-800 truncate">{service.titre}</h3>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">✓ Actif</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2 line-clamp-2">{service.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>💰 {service.tarif || 0} XOF</span>
                        {service.localisation && <span>📍 {service.localisation}</span>}
                        {service.categorie && <span>📁 {service.categorie}</span>}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/services/${service.id}`}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition text-center"
                      >
                        👁️ Voir
                      </Link>
                      <button
                        onClick={() => setDeleteModal(service)}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de confirmation de suppression */}
        {deleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🗑️</span>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Supprimer ce service ?
                </h2>
                <p className="text-gray-600">
                  Vous êtes sur le point de supprimer :
                </p>
                <p className="font-semibold text-gray-800 mt-2">
                  "{deleteModal.titre}"
                </p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">
                  ⚠️ <strong>Attention :</strong> Cette action est irréversible. Toutes les données associées à ce service seront définitivement supprimées.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteService}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? '⏳ Suppression...' : '🗑️ Confirmer la suppression'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServices;
